import React, { useEffect, useMemo, useRef } from 'react';
import SimpleChatMessage from './SimpleChatMessage';
import { Message } from '@/types';

interface SimpleChatMessageListProps {
  messages: Message[];
  isLoading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  chatId: string;
  hideCodeBlockIfAttachment?: boolean;
  onRegenerate: (messageId: string) => void;
  isMemoryUpdating?: boolean;
}

const SimpleChatMessageList: React.FC<SimpleChatMessageListProps> = ({
  messages,
  isLoading,
  messagesEndRef,
  chatId,
  hideCodeBlockIfAttachment,
  onRegenerate,
  isMemoryUpdating,
}) => {
  const animationFrameRef = useRef<number>();

  const latestMessageSignature = useMemo(() => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) {
      return 'empty';
    }

    return [
      lastMessage.id,
      lastMessage.role,
      lastMessage.content ?? '',
      lastMessage.timestamp ?? '',
      JSON.stringify(lastMessage.metadata ?? null),
    ].join(':');
  }, [messages]);

  useEffect(() => {
    animationFrameRef.current = window.requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [latestMessageSignature, isLoading, messagesEndRef]);

  return (
    <div className="min-h-full px-2 py-4">
      <div className="flex flex-col gap-4">
        {messages.map((message) => (
          <SimpleChatMessage
            key={message.id}
            message={message}
            hideCodeBlockIfAttachment={hideCodeBlockIfAttachment}
            onRegenerate={onRegenerate}
          />
        ))}
        {isLoading && isMemoryUpdating ? (
          <div className="flex items-center justify-center py-2">
            <span className="text-sm text-muted-foreground">Updating Memory...</span>
          </div>
        ) : null}
        <div ref={messagesEndRef} className="h-0" />
      </div>
    </div>
  );
};

export default SimpleChatMessageList;